import { useEffect, useState, useCallback } from "react";
import { Pin, PinOff, EyeOff, Eye, Save, X } from "lucide-react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";

const STATUSES = ["", "published", "hidden", "pending"];

export default function AdminDiscussions() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null); // thread_id
  const [draft, setDraft] = useState({ title: "", body: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    api.get(`/admin/threads?${p.toString()}`).then((r) => setRows(r.data)).catch(() => {});
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const pin = async (t) => { await api.post(`/admin/threads/${t.thread_id}/pin`, { pinned: !t.pinned }); load(); };
  const setStat = async (t, s) => { await api.post(`/admin/threads/${t.thread_id}/status`, { status: s }); load(); };
  const startEdit = (t) => { setEditing(t.thread_id); setDraft({ title: t.title, body: t.body }); };
  const saveEdit = async (t) => { await api.patch(`/admin/threads/${t.thread_id}`, draft); setEditing(null); load(); };

  return (
    <AdminShell active="discussions" title="Discussions" subtitle="Search, pin, edit or hide any discussion.">
      <div className="flex flex-wrap gap-3 mb-5">
        <input data-testid="disc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, body or author…"
          className="flex-1 min-w-[220px] rounded-full border border-rose/40 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
        <select data-testid="disc-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-full border border-rose/40 px-4 py-2.5 text-sm bg-white">
          {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>

      <div className="space-y-3" data-testid="disc-list">
        {rows.length === 0 && <p className="text-mutedplum">No discussions found.</p>}
        {rows.map((t) => (
          <div key={t.thread_id} className="rounded-2xl bg-white border border-rose/30 p-4" data-testid={`disc-${t.thread_id}`}>
            {editing === t.thread_id ? (
              <div className="space-y-2">
                <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="disc-edit-title"
                  className="w-full rounded-lg border border-rose/40 px-3 py-2 text-sm" />
                <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="disc-edit-body" rows={4}
                  className="w-full rounded-lg border border-rose/40 px-3 py-2 text-sm" />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(t)} data-testid={`disc-save-${t.thread_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal px-4 py-2 text-sm font-semibold text-white"><Save className="h-4 w-4" /> Save</button>
                  <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 rounded-full border border-rose/50 px-4 py-2 text-sm text-plum"><X className="h-4 w-4" /> Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-xs text-mutedplum">
                  <span className={`px-2 py-0.5 rounded-full ${t.status === "published" ? "bg-teal/15 text-teal-dark" : t.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-plum/10 text-plum"}`}>{t.status}</span>
                  {t.pinned && <span className="px-2 py-0.5 rounded-full bg-rose/40 text-plum">pinned</span>}
                  <span>{t.category_slug} · by {t.author_name} · {t.reply_count} replies</span>
                </div>
                <h3 className="font-display text-lg text-plum mt-1">{t.title}</h3>
                <p className="mt-1 text-sm text-plum-ink/80 line-clamp-2">{t.body}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => pin(t)} data-testid={`disc-pin-${t.thread_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-rose/50 px-3 py-1.5 text-xs font-semibold text-plum hover:bg-rose-soft">
                    {t.pinned ? <><PinOff className="h-3.5 w-3.5" /> Unpin</> : <><Pin className="h-3.5 w-3.5" /> Pin</>}
                  </button>
                  <button onClick={() => startEdit(t)} data-testid={`disc-edit-${t.thread_id}`} className="rounded-full border border-rose/50 px-3 py-1.5 text-xs font-semibold text-plum hover:bg-rose-soft">Edit</button>
                  {t.status === "hidden" ? (
                    <button onClick={() => setStat(t, "published")} data-testid={`disc-show-${t.thread_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal px-3 py-1.5 text-xs font-semibold text-white"><Eye className="h-3.5 w-3.5" /> Restore</button>
                  ) : (
                    <button onClick={() => setStat(t, "hidden")} data-testid={`disc-hide-${t.thread_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-rose/50 px-3 py-1.5 text-xs font-semibold text-plum hover:bg-rose-soft"><EyeOff className="h-3.5 w-3.5" /> Hide</button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
